import React from 'react';

import { Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton from 'components/action-button';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import { useOrderDetails } from 'hooks/use-order-details';
import { OrderStatus, useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 18.75rem;
  padding: 2rem 0 3rem 0;

  ${Styles.desktop`
    max-width: 31.25rem;
  `}
`;

const Heading = styled(ModalHeading)`
  margin: 3rem 0 1rem 0;
`;

const Text = styled.p`
  margin: 0 0 3rem 0;
`;

interface IOrderCancelledModalProps {
  onDismiss: () => void;
  rbiOrderId?: string;
}

const OrderCancelledModal: React.FC<IOrderCancelledModalProps> = ({ onDismiss, rbiOrderId }) => {
  const {
    serverOrder,
    reorder: { reordering },
  } = useOrderContext();
  const orderId = rbiOrderId ?? serverOrder?.rbiOrderId;
  const { order, loading, error } = useOrderDetails(orderId);
  // A query can retain a previous order while the selected receipt loads.
  const selectedOrder =
    orderId && !loading && !error && order?.rbiOrderId === orderId ? order : undefined;
  const { handleReorderClick, disabled } = useReorder(selectedOrder);
  const { formatMessage } = useIntl();

  const modalHeading = formatMessage({ id: 'cancelledOrderTitle' });
  const modalBody = formatMessage({
    id:
      selectedOrder?.status === OrderStatus.REFUND_SUCCESSFUL
        ? 'cancelledOrderAction'
        : 'deliveryStatusOrderCancelled',
  });

  return (
    <Modal onDismiss={onDismiss}>
      <ModalContent>
        <Container>
          <Icon icon="sadFace" color="primary" width="9.5rem" height="9.5rem" aria-hidden />
          <Heading id="modal-heading">{modalHeading}</Heading>
          <Text>{modalBody}</Text>
          <ActionButton
            onClick={handleReorderClick}
            isLoading={reordering}
            disabled={!selectedOrder || disabled}
          >
            {formatMessage({ id: 'reorder' })}
          </ActionButton>
        </Container>
      </ModalContent>
    </Modal>
  );
};

export default OrderCancelledModal;
